<template>
  <a-layout class="main-layout">
    <a-layout-sider v-model:collapsed="collapsed" collapsible class="sidebar">
      <div class="logo">
        <h2 v-if="!collapsed">PersonaHub</h2>
        <h3 v-else>PH</h3>
      </div>
      <a-menu
        v-model:selectedKeys="selectedKeys"
        v-model:openKeys="openKeys"
        theme="dark"
        mode="inline"
        @click="handleMenuClick"
      >
        <!-- Ana Sayfa -->
        <a-menu-item key="dashboard">
          <dashboard-outlined />
          <span>Dashboard</span>
        </a-menu-item>
        
        <!-- Veri Kaynakları -->
        <a-sub-menu key="data-sources-menu">
          <template #icon>
            <database-outlined />
          </template>
          <template #title>Veri Kaynakları</template>
          <a-menu-item key="brands">
            <shop-outlined />
            <span>Markalar</span>
          </a-menu-item>
          <a-menu-item key="data-sources">
            <api-outlined />
            <span>Platform Bağlantıları</span>
          </a-menu-item>
          <a-menu-item key="platform-definitions">
            <code-outlined />
            <span>Platform Tanımları</span>
          </a-menu-item>
          <a-menu-item key="products">
            <shopping-outlined />
            <span>Ürünler</span>
          </a-menu-item>
          <a-menu-item key="categories">
            <tags-outlined />
            <span>Kategoriler</span>
          </a-menu-item>
        </a-sub-menu>
        
        <!-- Çıktılar & Entegrasyonlar -->
        <a-sub-menu key="integrations">
          <template #icon>
            <export-outlined />
          </template>
          <template #title>Çıktılar & Entegrasyon</template>
          <a-menu-item key="export-files">
            <file-text-outlined />
            <span>Export (Dosya)</span>
          </a-menu-item>
          <a-menu-item key="api-schedule">
            <api-outlined />
            <span>API Schedule</span>
          </a-menu-item>
          <a-menu-item key="templates">
            <code-outlined />
            <span>Template'ler</span>
          </a-menu-item>
          <a-menu-item key="api-endpoints">
            <link-outlined />
            <span>API Endpoint'leri</span>
          </a-menu-item>
        </a-sub-menu>
        
        <!-- İzleme & Loglar -->
        <a-sub-menu key="monitoring">
          <template #icon>
            <bar-chart-outlined />
          </template>
          <template #title>İzleme & Loglar</template>
          <a-menu-item key="jobs">
            <clock-circle-outlined />
            <span>İşlemler</span>
          </a-menu-item>
          <a-menu-item key="api-logs">
            <history-outlined />
            <span>Erişim Logları</span>
          </a-menu-item>
          <a-menu-item key="system-health">
            <heart-outlined />
            <span>Sistem Durumu</span>
          </a-menu-item>
          <a-menu-item key="cost-analysis">
            <dollar-outlined />
            <span>Maliyet Analizi</span>
          </a-menu-item>
        </a-sub-menu>
        
        <!-- Ayarlar -->
      </a-menu>
    </a-layout-sider>
    
    <a-layout>
      <a-layout-header class="header">
        <div class="header-content">
          <h2 class="page-title">{{ pageTitle }}</h2>
          <a-dropdown>
            <a class="user-menu" @click.prevent>
              <div class="user-avatar">
                <user-outlined />
              </div>
              <span class="user-email">{{ authStore.user?.email || 'User' }}</span>
              <down-outlined class="dropdown-icon" />
            </a>
            <template #overlay>
              <a-menu class="user-dropdown">
                <a-menu-item @click="handleLogout" class="logout-item">
                  <logout-outlined />
                  Çıkış Yap
                </a-menu-item>
              </a-menu>
            </template>
          </a-dropdown>
        </div>
      </a-layout-header>
      
      <a-layout-content class="content">
        <router-view />
      </a-layout-content>
      
      <a-layout-footer class="footer">
        PersonaHub ©2025 - Multi-Brand E-Commerce Integration Platform
      </a-layout-footer>
    </a-layout>
  </a-layout>
</template>

<script>
import { ref, computed, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import {
  DashboardOutlined,
  ShopOutlined,
  ShoppingOutlined,
  TagsOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  CodeOutlined,
  HistoryOutlined,
  ApiOutlined,
  UserOutlined,
  DownOutlined,
  LogoutOutlined,
  DatabaseOutlined,
  ExportOutlined,
  BarChartOutlined,
  HeartOutlined,
  LinkOutlined,
  DollarOutlined,
  SettingOutlined,
  ToolOutlined
} from '@ant-design/icons-vue'

export default {
  name: 'MainLayout',
  components: {
    DashboardOutlined,
    ShopOutlined,
    ShoppingOutlined,
    TagsOutlined,
    ClockCircleOutlined,
    FileTextOutlined,
    CodeOutlined,
    HistoryOutlined,
    ApiOutlined,
    UserOutlined,
    DownOutlined,
    LogoutOutlined,
    DatabaseOutlined,
    ExportOutlined,
    BarChartOutlined,
    HeartOutlined,
    LinkOutlined,
    DollarOutlined,
    SettingOutlined,
    ToolOutlined
  },
  setup() {
    const router = useRouter()
    const route = useRoute()
    const authStore = useAuthStore()
    
    // localStorage'dan collapse durumunu al, yoksa false (açık)
    const savedCollapsed = localStorage.getItem('menuCollapsed')
    const collapsed = ref(savedCollapsed ? JSON.parse(savedCollapsed) : false)
    
    const selectedKeys = ref([route.name?.toLowerCase() || 'dashboard'])
    
    // localStorage'dan açık menüleri al, yoksa boş array (hepsi kapalı)
    const savedOpenKeys = localStorage.getItem('menuOpenKeys')
    const openKeys = ref(savedOpenKeys ? JSON.parse(savedOpenKeys) : [])
    
    const pageTitles = {
      'dashboard': 'Dashboard',
      'brands': 'Markalar',
      'data-sources': 'Platform Bağlantıları',
      'platform-definitions': 'Platform Tanımları',
      'products': 'Ürünler',
      'categories': 'Kategoriler',
      'jobs': 'İşlemler',
      'export-files': 'Export (Dosya Çıktısı)',
      'api-schedule': 'API Schedule',
      'templates': 'Template\'ler',
      'api-logs': 'Erişim Logları',
      'apilogs': 'Erişim Logları',
      'api-endpoints': 'API Endpoint\'leri',
      'apiendpoints': 'API Endpoint\'leri',
      'system-health': 'Sistem Durumu',
      'systemhealth': 'Sistem Durumu',
      'cost-analysis': 'Maliyet Analizi',
      'costanalysis': 'Maliyet Analizi',
    }
    
    const pageTitle = computed(() => {
      return pageTitles[selectedKeys.value[0]] || 'Dashboard'
    })
    
    const handleMenuClick = ({ key }) => {
      selectedKeys.value = [key]
      
      // Route name mapping
      const routeMapping = {
        'api-logs': 'ApiLogs',
        'api-endpoints': 'ApiEndpoints',
        'dashboard': 'Dashboard',
        'brands': 'Brands',
        'data-sources': 'DataSources',
        'platform-definitions': 'PlatformDefinitions',
        'products': 'Products',
        'categories': 'Categories',
        'jobs': 'Jobs',
        'export-files': 'ExportFiles',
        'api-schedule': 'ApiSchedule',
        'templates': 'Templates',
        'system-health': 'SystemHealth',
        'cost-analysis': 'CostAnalysis',
      }
      
      const routeName = routeMapping[key] || key.charAt(0).toUpperCase() + key.slice(1)
      router.push({ name: routeName })
    }
    
    const handleLogout = () => {
      authStore.logout()
    }
    
    // openKeys değiştiğinde localStorage'a kaydet
    watch(openKeys, (newValue) => {
      localStorage.setItem('menuOpenKeys', JSON.stringify(newValue))
    }, { deep: true })
    
    // collapsed değiştiğinde localStorage'a kaydet
    watch(collapsed, (newValue) => {
      localStorage.setItem('menuCollapsed', JSON.stringify(newValue))
    })
    
    return {
      collapsed,
      selectedKeys,
      openKeys,
      pageTitle,
      authStore,
      handleMenuClick,
      handleLogout
    }
  }
}
</script>

<style scoped>
/* Main Layout */
.main-layout {
  min-height: 100vh;
}

/* Sidebar */
.sidebar :deep(.ant-layout-sider) {
  background: linear-gradient(180deg, #667eea 0%, #764ba2 50%, #5a67d8 100%);
  box-shadow: 2px 0 12px rgba(102, 126, 234, 0.2);
}

.sidebar :deep(.ant-layout-sider-trigger) {
  background: linear-gradient(135deg, #5a67d8, #667eea);
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
}

.sidebar :deep(.ant-layout-sider-trigger:hover) {
  background: linear-gradient(135deg, #667eea, #764ba2);
}

.logo {
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.logo h2,
.logo h3 {
  color: white;
  margin: 0;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
}

/* Menu Styling */
:deep(.ant-menu-dark) {
  background: transparent;
  border-right: none;
}

:deep(.ant-menu-dark .ant-menu-item) {
  margin: 4px 8px;
  border-radius: 8px;
  transition: all 0.3s ease;
}

:deep(.ant-menu-dark .ant-menu-item-selected) {
  background: rgba(255, 255, 255, 0.25);
  color: #ffffff !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  font-weight: 600;
}

:deep(.ant-menu-dark .ant-menu-item-selected::after) {
  display: none;
}

:deep(.ant-menu-dark .ant-menu-item:hover) {
  background: rgba(255, 255, 255, 0.15);
  color: #ffffff;
}

:deep(.ant-menu-dark .ant-menu-submenu-title) {
  margin: 4px 8px;
  border-radius: 8px;
  transition: all 0.3s ease;
}

:deep(.ant-menu-dark .ant-menu-submenu-title:hover) {
  background: rgba(255, 255, 255, 0.15);
  color: #ffffff;
}

:deep(.ant-menu-dark .ant-menu-submenu-selected > .ant-menu-submenu-title) {
  color: #ffffff;
  background: rgba(255, 255, 255, 0.1);
}

:deep(.ant-menu-dark .ant-menu-sub) {
  background: rgba(0, 0, 0, 0.2);
}

:deep(.ant-menu-dark .ant-menu-item .anticon) {
  font-size: 16px;
}

:deep(.ant-menu-dark .ant-menu-submenu-arrow) {
  color: rgba(255, 255, 255, 0.85);
}

/* Header */
.header {
  background: #ffffff;
  padding: 0 32px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border-bottom: 1px solid #e8eaf6;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 100%;
}

.page-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #2d3748;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* User Menu */
.user-menu {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 16px;
  border-radius: 10px;
  transition: all 0.3s ease;
  background: #f7fafc;
  cursor: pointer;
}

.user-menu:hover {
  background: #edf2f7;
}

.user-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
}

.user-email {
  color: #4a5568;
  font-weight: 500;
  font-size: 14px;
}

.dropdown-icon {
  color: #718096;
  font-size: 12px;
}

/* Content */
.content {
  background: linear-gradient(135deg, #f5f7fa 0%, #e8eaf6 50%, #f5f7fa 100%);
  padding: 0;
  position: relative;
}

.content::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: 
    repeating-linear-gradient(0deg, transparent, transparent 50px, rgba(102, 126, 234, 0.02) 50px, rgba(102, 126, 234, 0.02) 51px),
    repeating-linear-gradient(90deg, transparent, transparent 50px, rgba(102, 126, 234, 0.02) 50px, rgba(102, 126, 234, 0.02) 51px);
  pointer-events: none;
}

/* Footer */
.footer {
  text-align: center;
  background: #ffffff;
  color: #718096;
  font-size: 13px;
  border-top: 1px solid #e2e8f0;
  padding: 16px 50px;
}

/* Responsive */
@media (max-width: 768px) {
  .header {
    padding: 0 16px;
  }
  
  .page-title {
    font-size: 16px;
  }
  
  .user-email {
    display: none;
  }
}
</style>

